import React, { useState, useRef, useEffect, useCallback } from 'react';

interface VideoProgressProps {
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
}

export const VideoProgress: React.FC<VideoProgressProps> = ({ currentTime, duration, onSeek }) => {
  const progressRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateProgress = (clientX: number) => {
    if (!progressRef.current) return 0;
    const rect = progressRef.current.getBoundingClientRect();
    const position = clientX - rect.left;
    const width = rect.width;
    return Math.max(0, Math.min(1, position / width));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    const progress = calculateProgress(e.clientX);
    onSeek(progress * duration);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    const progress = calculateProgress(e.clientX);
    onSeek(progress * duration);
  }, [isDragging, duration, onSeek]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const progressWidth = (currentTime / duration) * 100;

  return (
    <div 
      className="video-progress-container" 
      ref={progressRef} 
      onMouseDown={handleMouseDown}
      style={{
        position: 'relative',
        width: '100%',
        height: '8px',
        cursor: 'pointer',
        backgroundColor: 'rgba(255, 255, 255, 0.3)'
      }}
    >
      {/* Progress bar */}
      <div 
        className="video-progress-bar" 
        style={{ 
          width: `${progressWidth}%`,
          backgroundColor: '#ec2553',
          position: 'absolute',
          height: '100%',
          left: 0,
          top: 0
        }}
      >
        <div 
          className="video-progress-handle"
          style={{
            position: 'absolute',
            right: '-6px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '12px',
            height: '12px',
            backgroundColor: '#fff',
            borderRadius: '50%',
            opacity: isDragging ? 1 : 0,
            transition: 'opacity 0.2s'
          }}
        />
      </div>
    </div>
  );
};
